import React, { type ReactNode } from 'react';
import { cn } from '@lib/utils';
import { ImgWithFallback } from './ui/img-with-fallback';

interface SpaceAvatarChipProps {
  name: string;
  imageUrl?: string | null;
  /** Lit gradient ring for the selected state (My Calendars strip). Default false. */
  active?: boolean;
  /**
   * Reserve the 3px ring slot even when inactive, so toggling `active` only swaps the ring color and
   * never changes the chip's footprint (no layout shift on tap). Set by selectable strips
   * (MyCalendarsSpaceStrip); navigate-only strips (SpacesCarousel) omit it and keep the 58px circle.
   */
  selectable?: boolean;
  /**
   * Optional wrapper applied around JUST the circular avatar (not the name label) — e.g. the live
   * `LiveAvatarRing`. The ring encircles the 58px circle only; the name stays below it. Identity by
   * default (no wrapper), so non-decorated callers are byte-unchanged.
   */
  avatarDecorator?: (avatar: ReactNode) => ReactNode;
}

/**
 * Presentational 58px circular space avatar (image or initial fallback) + truncated name.
 * Shared by SpacesCarousel (navigate) and MyCalendarsSpaceStrip (select). Renders a fragment;
 * the caller wraps it in the flex-column button.
 */
export const SpaceAvatarChip: React.FC<SpaceAvatarChipProps> = ({
  name,
  imageUrl,
  active = false,
  selectable = false,
  avatarDecorator,
}) => {
  // Selected state: teal->periwinkle gradient ring (Figma Ellipse 181, sw=3). `selectable` always
  // reserves the 3px slot (transparent until active) so selection never resizes the chip;
  // navigate-only callers omit it and keep the plain 58px circle.
  const avatar = (
    <div
      className={cn(
        'rounded-full',
        selectable && 'p-[3px]',
        active && 'bg-gradient-to-b from-mb-teal to-mb-periwinkle',
      )}
    >
      <div className="w-[58px] h-[58px] rounded-full overflow-hidden bg-gray-200">
        {/* Decorative, on BOTH branches. Every caller wraps this chip in a button carrying its
            own explicit aria-label (SpacesCarousel "Go to {name}", MyCalendarsSpaceStrip
            "Filter by {name}"), which overrides the contents for the accessible name — so
            neither the photo nor the initial has a name to contribute. `alt=""` covers the
            image; the fallback needs its own aria-hidden, because ImgWithFallback passes no
            attributes down the fallback path. Without it a failed photo would put a stray
            "F" into the a11y tree on the error path only. */}
        <ImgWithFallback
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          fallback={() => (
            <div
              aria-hidden="true"
              className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600 text-lg font-semibold"
            >
              {name.charAt(0).toUpperCase()}
            </div>
          )}
        />
      </div>
    </div>
  );

  return (
    <>
      {avatarDecorator ? avatarDecorator(avatar) : avatar}
      <span className="text-xs font-semibold text-gray-900 max-w-[70px] truncate text-center">
        {name}
      </span>
    </>
  );
};
